import { PassiveIncomeMetrics } from "./passiveIncome";
import { convertCurrency } from "./currency";
import { resolveTransactionUnitPrice } from "./costBasis";
import { normalizeMoneyUnit } from "./quoteNormalization";
import { Transaction } from "../types";

interface SelectedPortfolioHeaderInput {
  currencySymbol: string;
  totalValue: number;
  totalInvested: number;
  totalProfit: number;
  totalReturnPct: number;
  dividendsReceived: number;
  passiveIncome: Pick<PassiveIncomeMetrics, "forwardAnnualDividend">;
  transactions: Transaction[];
  portfolioCurrency?: string;
  now?: Date;
}

export interface SelectedPortfolioHeaderCard {
  title: string;
  value: string;
  subValue?: string;
  secondaryValue?: string;
  tooltip: string;
}

interface DatedCashflow {
  date: Date;
  amount: number;
}

const DAYS_PER_YEAR = 365;
const MILLISECONDS_PER_DAY = 1000 * 3600 * 24;
const MAX_XIRR_ITERATIONS = 100;
const XIRR_TOLERANCE = 1e-7;

const formatMoney = (currencySymbol: string, value: number) =>
  `${currencySymbol}${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatSignedMoney = (currencySymbol: string, value: number) =>
  `${value >= 0 ? "+" : "-"}${formatMoney(currencySymbol, Math.abs(value))}`;

const formatSignedPercent = (value: number) =>
  `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;

export type CashflowIgnoreReason =
  | "missingDate"
  | "invalidDate"
  | "unsupportedAction"
  | "missingAmount"
  | "invalidAmount"
  | "missingCurrencyConversion"
  | "zeroAmount";

type NormalizedTransactionAction = "Buy" | "Sell" | "Dividend";

type PortfolioIrrFallbackReason =
  | "none"
  | "noTransactions"
  | "noValidCashflows"
  | "missingCurrentPortfolioValue"
  | "tooEarlyToCalculate"
  | "missingTransactionDates"
  | "missingTransactionAmounts"
  | "currencyConversionUnavailable"
  | "solverUnavailable";

interface TransactionCashflowResult {
  amount: number | null;
  reason?: CashflowIgnoreReason;
}

interface TransactionDateResult {
  date: Date | null;
  reason?: Extract<CashflowIgnoreReason, "missingDate" | "invalidDate">;
}

interface IgnoredTransactionSample {
  id?: string;
  symbol?: string;
  action?: unknown;
  date?: unknown;
  reason: CashflowIgnoreReason;
}

export interface PortfolioIrrDiagnostics {
  portfolioCurrency: string;
  transactionCount: number;
  currentPortfolioMarketValue: number;
  validCashflowCount: number;
  ignoredCount: number;
  ignoredReasons: Record<CashflowIgnoreReason, number>;
  sampleValidCashflows: Array<{ date: string; amount: number }>;
  sampleIgnoredTransactions: IgnoredTransactionSample[];
  cashflows: DatedCashflow[];
  firstCashflowDate: string | null;
  lastCashflowDate: string | null;
  daySpan: number | null;
  hasNegativeCashflow: boolean;
  hasPositiveCashflow: boolean;
  xirr: number | null;
  fallbackReason: PortfolioIrrFallbackReason;
}

type TransactionCashflowField =
  | "amountBase"
  | "totalBase"
  | "totalInBaseCurrency"
  | "valueBase"
  | "costBasisBase"
  | "baseAmount"
  | "convertedAmount";

type TransactionWithAppFields = Transaction &
  Record<TransactionCashflowField, unknown> & {
    type?: unknown;
    side?: unknown;
    transactionType?: unknown;
    time?: unknown;
    timestamp?: unknown;
    executedAt?: unknown;
  };

const BASE_AMOUNT_FIELDS: TransactionCashflowField[] = [
  "amountBase",
  "totalBase",
  "totalInBaseCurrency",
  "valueBase",
  "costBasisBase",
  "baseAmount",
  "convertedAmount",
];

const KNOWN_CONVERTIBLE_CURRENCIES = new Set(["GBP", "GBX", "GBXP", "USD", "EUR"]);

const emptyIgnoredReasons = (): Record<CashflowIgnoreReason, number> => ({
  missingDate: 0,
  invalidDate: 0,
  unsupportedAction: 0,
  missingAmount: 0,
  invalidAmount: 0,
  missingCurrencyConversion: 0,
  zeroAmount: 0,
});

const normalizeCurrencyCode = (value: unknown, fallback = "GBP") =>
  String(value || fallback)
    .trim()
    .toUpperCase();

const toFiniteNumber = (value: unknown) => {
  if (value === null || value === undefined || value === "") return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const readPositiveAmount = (value: unknown): TransactionCashflowResult => {
  if (value === null || value === undefined || value === "") {
    return { amount: null, reason: "missingAmount" };
  }

  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) {
    return { amount: null, reason: "invalidAmount" };
  }

  if (numberValue === 0) return { amount: null, reason: "zeroAmount" };
  if (numberValue < 0) return { amount: null, reason: "invalidAmount" };
  return { amount: Math.abs(numberValue) };
};

const getRawTransactionDate = (transaction: Transaction) => {
  const tx = transaction as TransactionWithAppFields;
  return (
    tx.date ||
    tx.time ||
    tx.timestamp ||
    tx.executedAt ||
    tx.snapshotAsOf ||
    tx.createdAt
  );
};

const getTransactionDateResult = (transaction: Transaction): TransactionDateResult => {
  const rawDate = getRawTransactionDate(transaction);
  if (!rawDate) return { date: null, reason: "missingDate" };

  const date = new Date(rawDate as string | number | Date);
  return Number.isFinite(date.getTime())
    ? { date }
    : { date: null, reason: "invalidDate" };
};

export const getTransactionDate = (transaction: Transaction) =>
  getTransactionDateResult(transaction).date;

const parseCashflowDate = (date: Date) => {
  const timestamp = date.getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const getTransactionAction = (
  transaction: Transaction,
): NormalizedTransactionAction | null => {
  const tx = transaction as TransactionWithAppFields;
  const rawAction = tx.action || tx.type || tx.side || tx.transactionType;
  const normalized = String(rawAction || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ");

  if (!normalized) return null;
  if (normalized.includes("buy")) return "Buy";
  if (normalized.includes("sell")) return "Sell";
  if (
    normalized === "dividend" ||
    normalized === "income" ||
    normalized.includes("dividend") ||
    normalized.includes("interest") ||
    normalized.includes("income")
  ) {
    return "Dividend";
  }

  return null;
};

const getFirstExplicitBaseAmount = (
  transaction: TransactionWithAppFields,
): TransactionCashflowResult => {
  let sawInvalidField: TransactionCashflowResult | null = null;

  for (const field of BASE_AMOUNT_FIELDS) {
    const value = transaction[field];
    if (value === null || value === undefined || value === "") continue;

    const amount = readPositiveAmount(value);
    if (amount.amount !== null) return amount;
    sawInvalidField = amount;
  }

  return sawInvalidField || { amount: null, reason: "missingAmount" };
};

const getTransactionFxRate = (transaction: Transaction) => {
  const fx = toFiniteNumber(transaction.originalExchangeRate) ?? toFiniteNumber(transaction.fxRate);
  return fx !== null && fx > 0 ? fx : null;
};

const isKnownConvertibleCurrency = (currency: string) => {
  const normalized = normalizeCurrencyCode(currency);
  return KNOWN_CONVERTIBLE_CURRENCIES.has(normalized) || normalized.startsWith("GBP");
};

const convertTransactionAmount = (
  amount: number,
  fromCurrencyRaw: unknown,
  toCurrencyRaw: unknown,
  transaction: Transaction,
) => {
  const fromCurrency = normalizeCurrencyCode(fromCurrencyRaw);
  const toCurrency = normalizeCurrencyCode(toCurrencyRaw);

  if (fromCurrency === toCurrency) return amount;

  const fx = getTransactionFxRate(transaction);
  const priceCurrency = normalizeCurrencyCode(
    transaction.originalPriceCurrency || transaction.priceCurrency,
    "",
  );
  const totalCurrency = normalizeCurrencyCode(
    transaction.totalCurrency || transaction.currency || transaction.costCurrency,
    "",
  );

  if (fx !== null) {
    if (fromCurrency === totalCurrency && toCurrency === priceCurrency) {
      return amount * fx;
    }
    if (fromCurrency === priceCurrency && toCurrency === totalCurrency) {
      return amount / fx;
    }
    if (fromCurrency === "GBP" && toCurrency !== "GBP") return amount * fx;
    if (fromCurrency !== "GBP" && toCurrency === "GBP") return amount / fx;
  }

  if (
    isKnownConvertibleCurrency(fromCurrency) &&
    isKnownConvertibleCurrency(toCurrency)
  ) {
    return convertCurrency(amount, fromCurrency, toCurrency);
  }

  return null;
};

const resolveAmountCandidateInBase = (
  transaction: Transaction,
  amountValue: unknown,
  sourceCurrency: unknown,
  baseCurrency: string,
): TransactionCashflowResult => {
  const amount = readPositiveAmount(amountValue);
  if (amount.amount === null) return amount;

  const converted = convertTransactionAmount(
    amount.amount,
    sourceCurrency,
    baseCurrency,
    transaction,
  );

  if (converted === null) {
    return { amount: null, reason: "missingCurrencyConversion" };
  }

  if (!Number.isFinite(converted)) {
    return { amount: null, reason: "invalidAmount" };
  }

  if (converted === 0) return { amount: null, reason: "zeroAmount" };
  if (converted < 0) return { amount: null, reason: "invalidAmount" };
  return { amount: Math.abs(converted) };
};

const getStoredTotalOrAmountInBase = (
  transaction: Transaction,
  baseCurrency: string,
): TransactionCashflowResult => {
  if (transaction.total !== undefined && transaction.total !== null) {
    return resolveAmountCandidateInBase(
      transaction,
      transaction.total,
      transaction.totalCurrency || transaction.currency || transaction.costCurrency,
      baseCurrency,
    );
  }

  if (transaction.amount !== undefined && transaction.amount !== null) {
    return resolveAmountCandidateInBase(
      transaction,
      transaction.amount,
      transaction.currency || transaction.totalCurrency || transaction.costCurrency,
      baseCurrency,
    );
  }

  return { amount: null, reason: "missingAmount" };
};

const getNormalizedPriceCurrency = (transaction: Transaction) => {
  const rawPrice =
    toFiniteNumber(transaction.originalPricePerShare) ??
    toFiniteNumber(transaction.pricePerShare) ??
    toFiniteNumber(transaction.price);
  const rawCurrency =
    transaction.originalPriceCurrency ||
    transaction.priceCurrency ||
    transaction.currency ||
    transaction.totalCurrency ||
    transaction.costCurrency;

  return normalizeMoneyUnit({
    value: rawPrice ?? 0,
    rawCurrency: rawCurrency || "GBP",
    exchange: transaction.exchange,
    symbol: transaction.symbol,
  }).currency;
};

const deriveTradeAmountInBase = (
  transaction: Transaction,
  baseCurrency: string,
): TransactionCashflowResult => {
  const quantity =
    toFiniteNumber(transaction.quantity) ?? toFiniteNumber(transaction.shares);
  if (quantity === null) return { amount: null, reason: "missingAmount" };
  if (quantity === 0) return { amount: null, reason: "zeroAmount" };
  if (quantity < 0) return { amount: null, reason: "invalidAmount" };

  const unitPrice = resolveTransactionUnitPrice(transaction);
  if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
    return unitPrice === 0
      ? { amount: null, reason: "zeroAmount" }
      : { amount: null, reason: "invalidAmount" };
  }

  const principalCurrency = getNormalizedPriceCurrency(transaction);
  const principalInBase = convertTransactionAmount(
    Math.abs(quantity) * unitPrice,
    principalCurrency,
    baseCurrency,
    transaction,
  );

  if (principalInBase === null) {
    return { amount: null, reason: "missingCurrencyConversion" };
  }

  const feeCurrency =
    transaction.currency || transaction.totalCurrency || transaction.costCurrency || principalCurrency;
  const fees = Math.max(toFiniteNumber(transaction.fees) ?? 0, 0);
  const tax = Math.max(toFiniteNumber(transaction.tax) ?? 0, 0);
  const feesInBase = convertTransactionAmount(fees + tax, feeCurrency, baseCurrency, transaction);

  if (feesInBase === null) {
    return { amount: null, reason: "missingCurrencyConversion" };
  }

  const amount = principalInBase + feesInBase;
  if (!Number.isFinite(amount)) return { amount: null, reason: "invalidAmount" };
  if (amount === 0) return { amount: null, reason: "zeroAmount" };
  if (amount < 0) return { amount: null, reason: "invalidAmount" };
  return { amount: Math.abs(amount) };
};

export const getTransactionBaseCashflowAmount = (
  transaction: Transaction,
  baseCurrency = "GBP",
): TransactionCashflowResult => {
  const action = getTransactionAction(transaction);
  if (!action) return { amount: null, reason: "unsupportedAction" };

  const explicitBaseAmount = getFirstExplicitBaseAmount(
    transaction as TransactionWithAppFields,
  );
  let amount = explicitBaseAmount.amount;
  let reason = explicitBaseAmount.reason;

  if (amount === null && reason === "missingAmount") {
    const storedAmount = getStoredTotalOrAmountInBase(transaction, baseCurrency);
    amount = storedAmount.amount;
    reason = storedAmount.reason;
  }

  if (amount === null && (reason === "missingAmount" || reason === "zeroAmount")) {
    const derivedAmount = deriveTradeAmountInBase(transaction, baseCurrency);
    amount = derivedAmount.amount;
    reason = derivedAmount.reason;
  }

  if (amount === null) return { amount: null, reason };

  return { amount: action === "Buy" ? -amount : amount };
};

export const getTransactionCashflowAmount = getTransactionBaseCashflowAmount;

const sampleCashflow = (cashflow: DatedCashflow) => ({
  date: cashflow.date.toISOString(),
  amount: cashflow.amount,
});

const sampleIgnoredTransaction = (
  transaction: Transaction,
  reason: CashflowIgnoreReason,
): IgnoredTransactionSample => ({
  id: transaction.id,
  symbol: transaction.symbol || transaction.displaySymbol,
  action: (transaction as TransactionWithAppFields).action,
  date: getRawTransactionDate(transaction),
  reason,
});

const canLoadBrowserDiagnosticsLogger = () =>
  typeof window !== "undefined" &&
  !(typeof process !== "undefined" && process.env?.JEST_WORKER_ID);

const logPortfolioIrrDiagnostics = (diagnostics: PortfolioIrrDiagnostics) => {
  if (!canLoadBrowserDiagnosticsLogger()) return;

  void import("./portfolioIrrDiagnosticsLogger")
    .then(({ logPortfolioIrrDiagnostics: logDiagnostics }) => {
      logDiagnostics(diagnostics);
    })
    .catch(() => undefined);
};

const hasIgnoredReason = (
  ignoredReasons: Record<CashflowIgnoreReason, number>,
  reasons: CashflowIgnoreReason[],
) => reasons.some((reason) => ignoredReasons[reason] > 0);

const hasOnlyIgnoredReasons = (
  ignoredReasons: Record<CashflowIgnoreReason, number>,
  reasons: CashflowIgnoreReason[],
) => {
  const allowed = new Set(reasons);
  return Object.entries(ignoredReasons).every(
    ([reason, count]) => count === 0 || allowed.has(reason as CashflowIgnoreReason),
  );
};

const allCashflowsShareOneDate = (cashflows: DatedCashflow[]) => {
  const timestamps = cashflows
    .map((cashflow) => cashflow.date.getTime())
    .filter(Number.isFinite);
  return timestamps.length >= 2 && new Set(timestamps).size === 1;
};

const getFallbackReason = (
  transactions: Transaction[],
  transactionCashflows: DatedCashflow[],
  cashflows: DatedCashflow[],
  currentPortfolioMarketValue: number,
  ignoredReasons: Record<CashflowIgnoreReason, number>,
  xirr: number | null,
): PortfolioIrrFallbackReason => {
  if (xirr !== null) return "none";
  if (!transactions.length) return "noTransactions";

  if (transactionCashflows.length === 0) {
    if (
      hasIgnoredReason(ignoredReasons, ["missingDate", "invalidDate"]) &&
      hasOnlyIgnoredReasons(ignoredReasons, ["missingDate", "invalidDate"])
    ) {
      return "missingTransactionDates";
    }

    if (hasIgnoredReason(ignoredReasons, ["missingCurrencyConversion"])) {
      return "currencyConversionUnavailable";
    }

    if (
      hasIgnoredReason(ignoredReasons, ["missingAmount", "invalidAmount"]) &&
      hasOnlyIgnoredReasons(ignoredReasons, ["missingAmount", "invalidAmount", "zeroAmount"])
    ) {
      return "missingTransactionAmounts";
    }

    return "noValidCashflows";
  }

  if (
    transactionCashflows.some((cashflow) => cashflow.amount < 0) &&
    !cashflows.some((cashflow) => cashflow.amount > 0) &&
    (!Number.isFinite(currentPortfolioMarketValue) || currentPortfolioMarketValue <= 0)
  ) {
    return "missingCurrentPortfolioValue";
  }

  if (allCashflowsShareOneDate(cashflows)) return "tooEarlyToCalculate";

  if (
    !hasRequiredCashflowSigns(cashflows) &&
    hasIgnoredReason(ignoredReasons, ["missingDate", "invalidDate"])
  ) {
    return "missingTransactionDates";
  }

  if (
    !hasRequiredCashflowSigns(cashflows) &&
    hasIgnoredReason(ignoredReasons, ["missingAmount", "invalidAmount"])
  ) {
    return "missingTransactionAmounts";
  }

  if (hasIgnoredReason(ignoredReasons, ["missingCurrencyConversion"])) {
    return "currencyConversionUnavailable";
  }

  return "solverUnavailable";
};

export const buildPortfolioIrrDiagnostics = (
  transactions: Transaction[],
  currentPortfolioMarketValue: number,
  now = new Date(),
  baseCurrency = "GBP",
): PortfolioIrrDiagnostics => {
  const ignoredReasons = emptyIgnoredReasons();
  const sampleIgnoredTransactions: IgnoredTransactionSample[] = [];

  const transactionCashflows = transactions
    .filter((transaction) => !transaction.deletedAt)
    .map((transaction) => {
      const dateResult = getTransactionDateResult(transaction);
      const cashflowAmount = getTransactionBaseCashflowAmount(
        transaction,
        baseCurrency,
      );

      const reason = dateResult.reason || cashflowAmount.reason;
      if (!dateResult.date || cashflowAmount.amount === null) {
        const ignoredReason = reason || "missingAmount";
        ignoredReasons[ignoredReason] += 1;
        if (sampleIgnoredTransactions.length < 5) {
          sampleIgnoredTransactions.push(
            sampleIgnoredTransaction(transaction, ignoredReason),
          );
        }
        return null;
      }

      return { date: dateResult.date, amount: cashflowAmount.amount };
    })
    .filter((cashflow): cashflow is DatedCashflow => cashflow !== null);

  const cashflows = [...transactionCashflows];
  const finalTimestamp = parseCashflowDate(now);
  if (
    finalTimestamp !== null &&
    Number.isFinite(currentPortfolioMarketValue) &&
    currentPortfolioMarketValue > 0
  ) {
    cashflows.push({
      date: new Date(finalTimestamp),
      amount: currentPortfolioMarketValue,
    });
  }

  const xirr = calculateXirr(cashflows);
  const ignoredCount = Object.values(ignoredReasons).reduce(
    (sum, count) => sum + count,
    0,
  );
  const sortedCashflowTimestamps = cashflows
    .map((cashflow) => cashflow.date.getTime())
    .filter(Number.isFinite)
    .sort((a, b) => a - b);
  const firstCashflowTimestamp = sortedCashflowTimestamps[0] ?? null;
  const lastCashflowTimestamp =
    sortedCashflowTimestamps[sortedCashflowTimestamps.length - 1] ?? null;

  return {
    portfolioCurrency: baseCurrency,
    transactionCount: transactions.length,
    currentPortfolioMarketValue,
    validCashflowCount: cashflows.length,
    ignoredCount,
    ignoredReasons,
    sampleValidCashflows: cashflows.slice(0, 5).map(sampleCashflow),
    sampleIgnoredTransactions,
    cashflows,
    firstCashflowDate:
      firstCashflowTimestamp === null
        ? null
        : new Date(firstCashflowTimestamp).toISOString(),
    lastCashflowDate:
      lastCashflowTimestamp === null
        ? null
        : new Date(lastCashflowTimestamp).toISOString(),
    daySpan:
      firstCashflowTimestamp === null || lastCashflowTimestamp === null
        ? null
        : (lastCashflowTimestamp - firstCashflowTimestamp) / MILLISECONDS_PER_DAY,
    hasNegativeCashflow: cashflows.some((cashflow) => cashflow.amount < 0),
    hasPositiveCashflow: cashflows.some((cashflow) => cashflow.amount > 0),
    xirr,
    fallbackReason: getFallbackReason(
      transactions,
      transactionCashflows,
      cashflows,
      currentPortfolioMarketValue,
      ignoredReasons,
      xirr,
    ),
  };
};

export const buildMoneyWeightedCashflows = (
  transactions: Transaction[],
  currentPortfolioMarketValue: number,
  now = new Date(),
  baseCurrency = "GBP",
): DatedCashflow[] => {
  const diagnostics = buildPortfolioIrrDiagnostics(
    transactions,
    currentPortfolioMarketValue,
    now,
    baseCurrency,
  );
  return diagnostics.cashflows;
};

const hasRequiredCashflowSigns = (cashflows: DatedCashflow[]) =>
  cashflows.some((cashflow) => cashflow.amount < 0) &&
  cashflows.some((cashflow) => cashflow.amount > 0);

const calculateNetPresentValue = (
  cashflows: DatedCashflow[],
  firstTimestamp: number,
  rate: number,
) =>
  cashflows.reduce((sum, cashflow) => {
    const years =
      (cashflow.date.getTime() - firstTimestamp) /
      (MILLISECONDS_PER_DAY * DAYS_PER_YEAR);
    return sum + cashflow.amount / Math.pow(1 + rate, years);
  }, 0);

const calculateNetPresentValueDerivative = (
  cashflows: DatedCashflow[],
  firstTimestamp: number,
  rate: number,
) =>
  cashflows.reduce((sum, cashflow) => {
    const years =
      (cashflow.date.getTime() - firstTimestamp) /
      (MILLISECONDS_PER_DAY * DAYS_PER_YEAR);

    if (years === 0) return sum;

    return sum - (years * cashflow.amount) / Math.pow(1 + rate, years + 1);
  }, 0);

const solveXirrWithNewton = (
  cashflows: DatedCashflow[],
  firstTimestamp: number,
) => {
  let rate = 0.1;

  for (let iteration = 0; iteration < MAX_XIRR_ITERATIONS; iteration += 1) {
    const npv = calculateNetPresentValue(cashflows, firstTimestamp, rate);

    if (Math.abs(npv) < XIRR_TOLERANCE) return rate;

    const derivative = calculateNetPresentValueDerivative(
      cashflows,
      firstTimestamp,
      rate,
    );

    if (!Number.isFinite(derivative) || Math.abs(derivative) < XIRR_TOLERANCE) {
      return null;
    }

    const nextRate = rate - npv / derivative;

    if (!Number.isFinite(nextRate) || nextRate <= -0.999999) {
      return null;
    }

    if (Math.abs(nextRate - rate) < XIRR_TOLERANCE) return nextRate;
    rate = nextRate;
  }

  return null;
};

const solveXirrWithBisection = (
  cashflows: DatedCashflow[],
  firstTimestamp: number,
) => {
  let lower = -0.999999;
  let upper = 10;
  let lowerNpv = calculateNetPresentValue(cashflows, firstTimestamp, lower);
  let upperNpv = calculateNetPresentValue(cashflows, firstTimestamp, upper);

  for (
    let expansion = 0;
    expansion < 20 && Math.sign(lowerNpv) === Math.sign(upperNpv);
    expansion += 1
  ) {
    upper *= 2;
    upperNpv = calculateNetPresentValue(cashflows, firstTimestamp, upper);
  }

  if (
    !Number.isFinite(lowerNpv) ||
    !Number.isFinite(upperNpv) ||
    Math.sign(lowerNpv) === Math.sign(upperNpv)
  ) {
    return null;
  }

  for (let iteration = 0; iteration < MAX_XIRR_ITERATIONS; iteration += 1) {
    const midpoint = (lower + upper) / 2;
    const midpointNpv = calculateNetPresentValue(
      cashflows,
      firstTimestamp,
      midpoint,
    );

    if (Math.abs(midpointNpv) < XIRR_TOLERANCE) return midpoint;

    if (Math.sign(midpointNpv) === Math.sign(lowerNpv)) {
      lower = midpoint;
      lowerNpv = midpointNpv;
    } else {
      upper = midpoint;
      upperNpv = midpointNpv;
    }
  }

  return (lower + upper) / 2;
};

export const calculateXirr = (cashflows: DatedCashflow[]) => {
  if (cashflows.length < 2 || !hasRequiredCashflowSigns(cashflows)) {
    return null;
  }

  const datedCashflows = cashflows
    .map((cashflow) => ({
      date: cashflow.date,
      amount: cashflow.amount,
      timestamp: cashflow.date.getTime(),
    }))
    .filter(
      (cashflow) =>
        Number.isFinite(cashflow.timestamp) && Number.isFinite(cashflow.amount),
    )
    .sort((a, b) => a.timestamp - b.timestamp);

  if (datedCashflows.length < 2 || !hasRequiredCashflowSigns(datedCashflows)) {
    return null;
  }

  const firstTimestamp = datedCashflows[0].timestamp;
  const lastTimestamp = datedCashflows[datedCashflows.length - 1].timestamp;

  if (firstTimestamp === lastTimestamp) return null;

  const normalizedCashflows = datedCashflows.map(({ date, amount }) => ({
    date,
    amount,
  }));

  return (
    solveXirrWithNewton(normalizedCashflows, firstTimestamp) ??
    solveXirrWithBisection(normalizedCashflows, firstTimestamp)
  );
};

export const isShortPortfolioHistory = (
  transactions: Transaction[],
  now = new Date(),
) => {
  if (transactions.length < 20) return true;

  const firstTransactionTimestamp = transactions.reduce<number | null>(
    (earliest, transaction) => {
      const date = getTransactionDate(transaction);
      const timestamp = date?.getTime() ?? null;
      if (timestamp === null) return earliest;
      return earliest === null ? timestamp : Math.min(earliest, timestamp);
    },
    null,
  );

  if (firstTransactionTimestamp === null) return false;

  const ageDays =
    (now.getTime() - firstTransactionTimestamp) / (1000 * 3600 * 24);
  return ageDays < 90;
};

export const buildSelectedPortfolioHeaderCards = ({
  currencySymbol,
  totalValue,
  totalInvested,
  totalProfit,
  totalReturnPct,
  dividendsReceived,
  passiveIncome,
  transactions,
  portfolioCurrency = "GBP",
  now = new Date(),
}: SelectedPortfolioHeaderInput): SelectedPortfolioHeaderCard[] => {
  const irrDiagnostics = buildPortfolioIrrDiagnostics(
    transactions,
    totalValue,
    now,
    portfolioCurrency,
  );
  logPortfolioIrrDiagnostics(irrDiagnostics);
  const xirr = irrDiagnostics.xirr;
  const isShortHistory = isShortPortfolioHistory(transactions, now);
  const simpleReturnText = `Simple return: ${formatSignedPercent(totalReturnPct)}`;
  const irrFallbackTextByReason: Record<PortfolioIrrFallbackReason, string> = {
    none: "Money-weighted return",
    noTransactions: "No transactions yet",
    noValidCashflows: "No valid cashflows found",
    missingCurrentPortfolioValue: "Missing current portfolio value",
    tooEarlyToCalculate: "Too early to annualise",
    missingTransactionDates: "Transaction dates missing",
    missingTransactionAmounts: "Transaction amounts missing",
    currencyConversionUnavailable: "Currency conversion unavailable",
    solverUnavailable: "IRR not meaningful yet",
  };
  const specificDataIssueReasons: PortfolioIrrFallbackReason[] = [
    "noTransactions",
    "noValidCashflows",
    "missingCurrentPortfolioValue",
    "missingTransactionDates",
    "missingTransactionAmounts",
    "currencyConversionUnavailable",
  ];
  const hasSpecificIrrDataIssue = specificDataIssueReasons.includes(
    irrDiagnostics.fallbackReason,
  );
  const irrFallbackText =
    xirr === null && isShortHistory && !hasSpecificIrrDataIssue
      ? "Too early to annualise"
      : irrFallbackTextByReason[irrDiagnostics.fallbackReason];
  const irrSecondaryValue =
    xirr === null && !hasSpecificIrrDataIssue
      ? simpleReturnText
      : isShortHistory
        ? "Short history — may be volatile"
        : undefined;

  return [
    {
      title: "Portfolio value",
      value: formatMoney(currencySymbol, totalValue),
      subValue: `${formatMoney(currencySymbol, totalInvested)} invested`,
      tooltip: "Current market value of selected portfolio.",
    },
    {
      title: "Total return",
      value: formatSignedMoney(currencySymbol, totalProfit),
      subValue: `${formatSignedPercent(totalReturnPct)} since buy`,
      tooltip:
        "Current value minus invested cost. Percentage is simple return since buy.",
    },
    {
      title: "IRR",
      value: xirr === null ? "—" : `${(xirr * 100).toFixed(2)}%`,
      subValue: xirr === null ? irrFallbackText : "Money-weighted return",
      secondaryValue: irrSecondaryValue,
      tooltip:
        "IRR is a money-weighted annualised return. For very new portfolios or same-day cashflows, simple return is more useful until more time has passed.",
    },
    {
      title: "Dividend income",
      value: `${formatMoney(currencySymbol, dividendsReceived)} received`,
      subValue: `${formatMoney(currencySymbol, passiveIncome.forwardAnnualDividend)} confirmed forward / year`,
      secondaryValue: "Insufficient history for annualised yield",
      tooltip:
        "Actual imported dividends received are shown first. Confirmed forward annual income only uses available provider/forecast data.",
    },
  ];
};
